import { questionTopicRequest } from './qbankNavigation';
import { QUESTION_BANK_ROUTES } from "./questionBankRoutes";
import { paidQuestionBlocked, questionSubscriptionTarget } from './paidQuestionFlow';
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import HomeDashboardLayout from "../homeDashboard/HomeDashboardLayout";
import ReportModal from "../components/ReportModal";
import StickyNotesPopup from "../components/StickyNotesPopup";
import ViewModal from "../components/ViewModal";
import {
  useQuestionBankInstructions,
  useQuestionBankPracticeMutations,
  useQuestionBankPracticePalette,
  useQuestionBankPracticeQuestions,
  useQuestionBankQuestionReference,
} from "../hooks/useQuestionBankPractice";
import { useQuestionBankQuestionActions } from "../hooks/useQuestionBankQuestionActions";
import {
  useQuestionBankQuestionFeedback,
  useQuestionNotes,
  useQuestionReports,
} from "../hooks/useQuestionBankQuestionFeedback";
import "./QuestionBankPractice.css";
import { localizedText, localizeQuestion, resetQuestion, practiceFilters, paletteQuestions } from "./practiceFlow";
import { useSidebarData } from "../hooks/useSidebarData";
import { resolveQuestionBankStateId } from "./stateUtils";
import {
  buildQuestionBankFlowState,
  getStoredQuestionBankFlowState,
  persistQuestionBankFlowState,
} from "./qbankFlowState";

const QUESTION_BANK_CATEGORY_ID = 2;
const DEFAULT_SUBJECT_ID = 1;

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const secs = String(seconds % 60).padStart(2, "0");
  return `${hrs}:${mins}:${secs}`;
}

function stripHtml(value) {
  return String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function toOptionTone(question, option, optionId) {
  const isSelected = Number(question?.selectedOptionId || 0) === Number(optionId);
  if (question?.isAnswered && option?.isCorrect) {
    return "correct";
  }
  if (question?.isAnswered && isSelected && !option?.isCorrect) {
    return "wrong";
  }
  return "neutral";
}

function toOptionLabel(index) {
  return String.fromCharCode(65 + index);
}

function mergeQuestion(previousQuestion, nextQuestion) {
  if (!previousQuestion) {
    return nextQuestion;
  }

  return {
    ...previousQuestion,
    ...nextQuestion,
    options: nextQuestion?.options?.length ? nextQuestion.options : previousQuestion?.options || [],
    raw: {
      ...(previousQuestion?.raw || {}),
      ...(nextQuestion?.raw || {}),
    },
  };
}

function normalizePaletteStatus(item, mergedQuestion, selectedOptionId) {
  if (mergedQuestion?.isAnswered || selectedOptionId) {
    const selectedOption = (mergedQuestion?.options || []).find(
      (option) => Number(option.id) === Number(selectedOptionId)
    );
    if (selectedOption?.isCorrect) {
      return "correct";
    }
    if (selectedOptionId) {
      return "incorrect";
    }
    return "solved";
  }
  return "unsolved";
}

function createPalettePlaceholder(sequence) {
  return {
    id: `placeholder-${sequence}`,
    question_id: 0,
    serialNo: sequence,
    question_index: sequence,
    question_number: sequence,
    question: `Question ${sequence}`,
    questionHtml: `Question ${sequence}`,
    status: "unsolved",
    isPlaceholder: true,
  };
}

function QuestionPalette({
  items,
  summaryItems = items,
  activeQuestionId,
  onSelect,
  canShowMore = false,
  onShowMore,
  activeTab = "grid",
  onTabChange,
  isLoadingMore = false,
}) {
  const gridListRef = useRef(null);
  const textListRef = useRef(null);
  const solvedCount = summaryItems.filter(
    (item) => item.status === "solved" || item.status === "correct" || item.status === "incorrect"
  ).length;
  const correctCount = summaryItems.filter((item) => item.status === "correct").length;
  const incorrectCount = summaryItems.filter((item) => item.status === "incorrect").length;
  const unsolvedCount = Math.max(0, summaryItems.length - solvedCount);

  useEffect(() => {
    const container = activeTab === "grid" ? gridListRef.current : textListRef.current;
    if (!container) {
      return;
    }
    const activeNode = container.querySelector('[data-active="true"]');
    if (activeNode && typeof activeNode.scrollIntoView === "function") {
      activeNode.scrollIntoView({ block: "center", inline: "nearest" });
    }
  }, [activeQuestionId, activeTab, items]);

  return (
    <div className="palette-container">
      <div className="qbank-paletteTabs">
        <button
          type="button"
          className={`qbank-paletteTab${activeTab === "grid" ? " is-active" : ""}`}
          onClick={() => onTabChange?.("grid")}
        >
          <i className="bi bi-grid-3x3-gap" /> Grid View
        </button>
        <button
          type="button"
          className={`qbank-paletteTab${activeTab === "list" ? " is-active" : ""}`}
          onClick={() => onTabChange?.("list")}
        >
          <i className="bi bi-list-task" /> List View
        </button>
      </div>

      <div className="palette-summary">
        <div className="summary-card solved">
          <div className="count green">{solvedCount}</div>
          <div><h6>Solved</h6></div>
        </div>
        <div className="summary-card unsolved">
          <div className="count gray">{unsolvedCount}</div>
          <div><h6>Unsolved</h6></div>
        </div>
        <div className="summary-card correct">
          <div className="count mint">{correctCount}</div>
          <div><h6>Correct</h6></div>
        </div>
        <div className="summary-card incorrect">
          <div className="count pink">{incorrectCount}</div>
          <div><h6>Incorrect</h6></div>
        </div>
      </div>

      {activeTab === "grid" ? (
        <div className="question-grid" ref={gridListRef}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              data-active={Number(activeQuestionId) === Number(item.id) ? "true" : "false"}
              className={`question-btn ${item.status} ${Number(activeQuestionId) === Number(item.id) ? "active" : ""}`}
              onClick={() => onSelect(item)}
            >
              {item.serialNo || item.serial || item.question_index || item.id}
              {item.isLocked || item.isPaidBlocked ? <i className="bi bi-lock-fill" aria-label="Paid question" /> : null}
            </button>
          ))}
        </div>
      ) : (
        <div className="qbank-paletteList" ref={textListRef}>
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              data-active={Number(activeQuestionId) === Number(item.id) ? "true" : "false"}
              className={`qbank-paletteList__item ${item.status} ${Number(activeQuestionId) === Number(item.id) ? "active" : ""}`}
              onClick={() => onSelect(item)}
            >
              <span className="qbank-paletteList__number">
                {item.serialNo || item.serial || item.question_index || item.id}
              </span>
              <span className="qbank-paletteList__text">
                {stripHtml(item.question || item.questionHtml || `Question ${index + 1}`)}
              </span>
            </button>
          ))}
        </div>
      )}

      {canShowMore ? (
        <button type="button" className="downl-btn question-more-btn" aria-label="Show more questions" onClick={onShowMore}>
          <i className={`bi ${isLoadingMore ? "bi-arrow-repeat" : "bi-chevron-down"}`} />
        </button>
      ) : null}

      <div className="bottom-btn">
        <button type="button" className="questions-btn">
          Questions
        </button>
      </div>
    </div>
  );
}

function QuestionBankPracticeShimmer() {
  return (
    <div className="categories-page qbank-detail-page qbank-practiceShimmer" style={{ background: "#fff" }}>
      <div className="categories-page-header">
        <div>
          <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__back" />
        </div>
        <div className="qbank-practiceShimmer__headerTitleWrap">
          <span className="dashboard-skeleton dashboard-skeleton--title qbank-practiceShimmer__headerTitle" />
        </div>
        <div>
          <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__profile" />
        </div>
      </div>

      <div className="qb-Detail-content">
        <div className="d-flex qbank-detail-main">
          <section className="qbank-LeftContent" aria-hidden="true">
            <div className="qbank-card qbank-card--question">
              <div className="qbank-questionTop">
                <span className="dashboard-skeleton dashboard-skeleton--title qbank-practiceShimmer__progress" />
                <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__timer" />
                <div className="qbank-toolbar">
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__tool" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__tool" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__tool" />
                </div>
              </div>

              <div className="data-question-scroll">
                <div className="qbank-questionBlock">
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__questionLine" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__questionLine qbank-practiceShimmer__questionLine--medium" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__questionLine qbank-practiceShimmer__questionLine--short" />
                </div>

                <div className="qbank-options">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div className="qbank-option qbank-practiceShimmer__option" key={index}>
                      <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__optionBadge" />
                      <div className="qbank-option__copy qbank-practiceShimmer__optionCopy">
                        <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__optionLine" />
                        <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__optionLine qbank-practiceShimmer__optionLine--short" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="qbank-guess qbank-practiceShimmer__guess">
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__guessCheck" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__guessLabel" />
                </div>
              </div>

              <div className="footer-nav-fixed footer-nav-fixed--desktop qbank-practiceShimmer__footer">
                <div className="qbank-navRow">
                  <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__navBtn" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__navMenu" />
                  <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__navBtn" />
                </div>
                <div className="qbank-stats">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__stat" key={index} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <aside className="qbank-rightContent" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, index) => (
              <section className="qbank-sideCard qbank-practiceShimmer__sideCard" key={index}>
                <div className="qbank-sideCard__head">
                  <span className="dashboard-skeleton dashboard-skeleton--title qbank-practiceShimmer__sideTitle" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__sideToggle" />
                </div>
                <div className="qbank-sideCard__body">
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__sideLine" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__sideLine qbank-practiceShimmer__sideLine--short" />
                </div>
              </section>
            ))}
          </aside>
        </div>

        <div className="Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--desktop" aria-hidden="true">
          <div className="palette-container qbank-practiceShimmer__palette">
            <div className="qbank-paletteTabs">
              <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__paletteTab" />
              <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__paletteTab" />
            </div>

            <div className="palette-summary">
              {Array.from({ length: 4 }).map((_, index) => (
                <div className="summary-card qbank-practiceShimmer__summaryCard" key={index}>
                  <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__summaryCount" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__summaryLabel" />
                </div>
              ))}
            </div>

            <div className="question-grid">
              {Array.from({ length: 15 }).map((_, index) => (
                <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__paletteItem" key={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const QuestionBankPractice = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: sidebarData = null } = useSidebarData();
  const storedFlowState = useMemo(() => getStoredQuestionBankFlowState(), []);
  const flowState = useMemo(
    () => buildQuestionBankFlowState(location.state, storedFlowState),
    [location.state, storedFlowState]
  );
  const initialQuestionId = Number(flowState?.question_id || 0);
  const initialQuestionSequence = Number(flowState?.question_index || 0);
  const initialSelectedQuestion = flowState?.selectedQuestion || null;
  const initialSelectedQuestionId = Number(initialSelectedQuestion?.id || initialQuestionId || 0);
  const initialQuestionsPayload = flowState?.initial_questions_payload || null;
  const initialQuestionsPage = Number(flowState?.initial_questions_page || 1);
  const answerInFlightRef = useRef(false);
  const localQuestionsRef = useRef(new Set());
  const [language, setLanguage] = useState(String(flowState.language || "en").toLowerCase() === "hi" ? "hi" : "en");
  const [fontSize, setFontSize] = useState(18);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== "hidden");
  const latestCurrentQuestionIdRef = useRef(0);
  const latestQuestionTimersRef = useRef({});
  const latestQuestionsByIdRef = useRef({});
  const fetchedPagesRef = useRef(new Set());
  const fetchingNextPageRef = useRef(false);
  const pageSizeRef = useRef(0);
  const additionalInfoAutoOpenedRef = useRef({});
  const enteredQuestionRef = useRef(0);

  const [currentPage, setCurrentPage] = useState(initialQuestionsPage || 1);
  const [currentQuestionId, setCurrentQuestionId] = useState(initialSelectedQuestionId || 0);
  const [questionsById, setQuestionsById] = useState(() =>
    initialSelectedQuestionId && initialSelectedQuestion ? { [initialSelectedQuestionId]: initialSelectedQuestion } : {}
  );
  const [questionOrder, setQuestionOrder] = useState(() =>
    initialSelectedQuestionId ? [initialSelectedQuestionId] : []
  );
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [guessFlags, setGuessFlags] = useState({});
  const [questionTimers, setQuestionTimers] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [isPaletteDrawerOpen, setIsPaletteDrawerOpen] = useState(false);
  const [isPaletteDrawerEnabled, setIsPaletteDrawerEnabled] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1199.98
  );
  const [isExplanationOpen, setIsExplanationOpen] = useState(true);
  const [isPYPOpen, setIsPYPOpen] = useState(true);
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [savingQuestionId, setSavingQuestionId] = useState(0);
  const [sidebarVisibleCount, setSidebarVisibleCount] = useState(50);
  const [pendingSidebarVisibleCount, setPendingSidebarVisibleCount] = useState(0);
  const [pendingNextIndex, setPendingNextIndex] = useState(null);
  const [pendingPaletteQuestionId, setPendingPaletteQuestionId] = useState(0);
  const [pendingPaletteSequence, setPendingPaletteSequence] = useState(0);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [paletteTab, setPaletteTab] = useState("grid");
  const practiceFlowState = useMemo(
    () =>
      buildQuestionBankFlowState({
        ...flowState,
        initial_questions_page: initialQuestionsPage,
        initial_questions_payload: initialQuestionsPayload,
        question_id: initialQuestionId,
        question_index: initialQuestionSequence,
      }),
    [flowState, initialQuestionId, initialQuestionSequence, initialQuestionsPage, initialQuestionsPayload]
  );

  useEffect(() => {
    persistQuestionBankFlowState(practiceFlowState);
  }, [practiceFlowState]);

  const resolvedStateId = useMemo(
    () =>
      resolveQuestionBankStateId({
        locationState: flowState,
        sidebarData,
      }),
    [flowState, sidebarData]
  );
  const questionBaseParams = useMemo(
    () => ({
      states_id: resolvedStateId,
      category_id: Number(flowState.category_id || QUESTION_BANK_CATEGORY_ID),
      ...practiceFilters(flowState),
      subject_id: Number(
        flowState?.selectedSubject?.id || flowState?.subject_id || DEFAULT_SUBJECT_ID
      ),
      ...questionTopicRequest(flowState),
      ...(Number(flowState?.exam_id || 0) > 0 ? { exam_id: Number(flowState.exam_id) } : {}),

      platform: "web",
    }),
    [flowState, resolvedStateId]
  );

  const questionQueryParams = useMemo(
    () => ({
      ...questionBaseParams,
      page: currentPage,
    }),
    [currentPage, questionBaseParams]
  );

  const shouldUseInitialQuestionsPayload = Boolean(
    initialQuestionsPayload &&
      initialQuestionsPage > 0 &&
      currentPage === initialQuestionsPage
  );

  const questionsQuery = useQuestionBankPracticeQuestions(questionQueryParams, {
    enabled: !shouldUseInitialQuestionsPayload,
  });
  const paletteQuery = useQuestionBankPracticePalette(questionBaseParams);
  const instructionsQuery = useQuestionBankInstructions(questionBaseParams, {
    enabled: isInstructionsOpen,
  });
  const { saveAnswer, saveGuess, saveTime } = useQuestionBankPracticeMutations();
  const { toggleBookmark, toggleLike } = useQuestionBankQuestionActions(questionQueryParams);
  const { deleteNote } = useQuestionBankQuestionFeedback(questionQueryParams);
  const activeQuestionsData = shouldUseInitialQuestionsPayload
    ? initialQuestionsPayload
    : questionsQuery.data;

  const currentQuestion = localizeQuestion(questionsById[currentQuestionId], language);
  latestCurrentQuestionIdRef.current = Number(currentQuestionId || 0);
  latestQuestionTimersRef.current = questionTimers;
  latestQuestionsByIdRef.current = questionsById;
  const currentQuestionAnswered = Boolean(currentQuestion?.isAnswered);
  const notesQuery = useQuestionNotes(Number(currentQuestion?.id || 0), {
    enabled: Boolean(currentQuestion?.id),
  });
  const reportsQuery = useQuestionReports(Number(currentQuestion?.id || 0));
  const referenceQuery = useQuestionBankQuestionReference(
    {
      ...questionBaseParams,
      question_id: Number(currentQuestion?.id || 0),
      language,
      is_correct: currentQuestion?.options?.find((option) => Number(option.id) === Number(currentQuestion.selectedOptionId))?.isCorrect ? 1 : 0,
    },
    {
      enabled: Boolean(currentQuestion?.id) && currentQuestionAnswered,
      fontSize,
    }
  );

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1199.98;
      setIsPaletteDrawerEnabled(isMobile);
      if (!isMobile) {
        setIsPaletteDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isPaletteDrawerOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isPaletteDrawerOpen]);

  useEffect(() => {
    const fetchedQuestions = activeQuestionsData?.questions || [];
    if (!fetchedQuestions.length) {
      return;
    }

    setQuestionsById((current) => {
      const next = { ...current };
      fetchedQuestions.forEach((question) => {
        next[question.id] = localQuestionsRef.current.has(question.id)
          ? current[question.id] : mergeQuestion(current[question.id], question);
      });
      return next;
    });

    setQuestionOrder((current) => {
      const next = [...current];
      fetchedQuestions.forEach((question) => {
        if (!next.includes(question.id)) {
          next.push(question.id);
        }
      });
      return next.sort((a, b) => {
        const all = [...fetchedQuestions, ...Object.values(latestQuestionsByIdRef.current)];
        return Number(all.find((q) => Number(q.id) === Number(a))?.serialNo || 0) - Number(all.find((q) => Number(q.id) === Number(b))?.serialNo || 0);
      });
    });

    setSelectedAnswers((current) => {
      const next = { ...current };
      fetchedQuestions.forEach((question) => {
        if (current[question.id] === undefined) {
          next[question.id] = Number(question.selectedOptionId || 0) || null;
        }
      });
      return next;
    });

    setGuessFlags((current) => {
      const next = { ...current };
      fetchedQuestions.forEach((question) => {
        if (current[question.id] === undefined) {
          next[question.id] = Boolean(question.isGuess);
        }
      });
      return next;
    });

    setQuestionTimers((current) => {
      const next = { ...current };
      fetchedQuestions.forEach((question) => {
        if (current[question.id] === undefined) {
          next[question.id] = Number(question.questionTime || 0);
        }
      });
      return next;
    });

    const pagination = activeQuestionsData?.pagination || {};
    const currentFetchedPage = Number(pagination?.currentPage || currentPage || 1);
    fetchedPagesRef.current.add(currentFetchedPage);
    fetchingNextPageRef.current = false;
    setIsFetchingNextPage(false);

    if (currentFetchedPage === 1 && fetchedQuestions.length > 0 && pageSizeRef.current === 0) {
      pageSizeRef.current = fetchedQuestions.length;
    }

    if (!currentQuestionId && !initialQuestionSequence) {
      setCurrentQuestionId(initialQuestionId || fetchedQuestions[0]?.id || 0);
    }
  }, [activeQuestionsData, currentPage, currentQuestionId, initialQuestionId, initialQuestionSequence]);

  useEffect(() => {
    if (!initialQuestionSequence || currentQuestionId) {
      return;
    }

    const matchedQuestionId = questionOrder.find((questionId) => {
      const question = questionsById[questionId];
      return Number(question?.serialNo || question?.question_index || 0) === initialQuestionSequence;
    });

    if (matchedQuestionId) {
      setCurrentQuestionId(Number(matchedQuestionId));
      return;
    }

    ensureQuestionSequenceLoaded(initialQuestionSequence);
  }, [currentQuestionId, initialQuestionSequence, questionOrder, questionsById]);

  useEffect(() => {
    if (!currentQuestion?.id || currentQuestionAnswered || !pageVisible || savingQuestionId) return undefined;
    const questionId = currentQuestion.id;
    const intervalId = window.setInterval(() => {
      if (answerInFlightRef.current || document.visibilityState === "hidden") return;
      setQuestionTimers((current) => {
        const next = { ...current, [questionId]: Number(current[questionId] || 0) + 1 };
        latestQuestionTimersRef.current = next;
        return next;
      });
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [currentQuestion?.id, currentQuestionAnswered, pageVisible, savingQuestionId]);

  const paletteItems = useMemo(() => {
    const serverItems = paletteQuestions(paletteQuery.data);
    const totalCount = Math.max(
      Number(activeQuestionsData?.totalQuestions || 0),
      Number(activeQuestionsData?.pagination?.totalRecords || 0),
      Number(paletteQuery.data?.total_questions || 0),
      Number(questionOrder.length || 0),
      serverItems.length
    );

    if (totalCount <= 0) {
      return questionOrder
        .map((questionId) => questionsById[questionId])
        .filter(Boolean)
        .map((item) => {
          const selectedOptionId = Number(selectedAnswers[item.id] || item?.selectedOptionId || 0);
          return {
            ...item,
            serial: String(item?.serialNo || item?.question_index || item?.id || 0).padStart(2, "0"),
            status: normalizePaletteStatus(item, item, selectedOptionId),
          };
        });
    }

    const loadedBySequence = {};
    questionOrder.forEach((questionId) => {
      const question = questionsById[questionId];
      const sequence = Number(question?.serialNo || question?.question_index || 0);
      if (sequence > 0) {
        loadedBySequence[sequence] = question;
      }
    });

    return Array.from({ length: totalCount }, (_, index) => {
      const sequence = index + 1;
      const loadedItem = loadedBySequence[sequence] || null;
      const serverItem = serverItems.find((item) => item.serialNo === sequence);
      const baseItem = loadedItem || serverItem || createPalettePlaceholder(sequence);
      const questionId = Number(loadedItem?.id || 0);
      const selectedOptionId = questionId
        ? Number(selectedAnswers[questionId] || loadedItem?.selectedOptionId || 0)
        : 0;

      return {
        ...baseItem,
        serialNo: sequence,
        question_index: sequence,
        question_number: sequence,
        serial: String(sequence).padStart(2, "0"),
        status: loadedItem ? normalizePaletteStatus(baseItem, loadedItem, selectedOptionId) : serverItem?.status || "unsolved",
      };
    });
  }, [activeQuestionsData, paletteQuery.data, questionOrder, questionsById, selectedAnswers]);

  const currentQuestionIndex = useMemo(
    () => questionOrder.findIndex((questionId) => Number(questionId) === Number(currentQuestionId)),
    [currentQuestionId, questionOrder]
  );

  const totalQuestionCount = Number(
    paletteItems.length ||
      activeQuestionsData?.totalQuestions ||
      activeQuestionsData?.pagination?.totalRecords ||
      questionOrder.length ||
      0
  );

  useEffect(() => {
    const currentSeq = Number(currentQuestion?.serialNo || currentQuestionIndex + 1 || 1);
    setSidebarVisibleCount((previous) => Math.max(previous, currentSeq));
  }, [currentQuestion?.serialNo, currentQuestionIndex]);

  useEffect(() => {
    const loadedCount = Number(questionOrder.length || 0);
    if (loadedCount > 0) {
      setSidebarVisibleCount((previous) => Math.max(previous, loadedCount));
    }
  }, [questionOrder.length]);

  useEffect(() => {
    const normalizedPendingVisibleCount = Number(pendingSidebarVisibleCount || 0);
    if (!normalizedPendingVisibleCount) {
      return;
    }

    const nextPage = Number(activeQuestionsData?.pagination?.nextPage || 0);
    const loadedCount = Number(questionOrder.length || 0);
    if (loadedCount < normalizedPendingVisibleCount && nextPage > 0) {
      return;
    }

    setSidebarVisibleCount((previous) =>
      Math.max(previous, Math.min(normalizedPendingVisibleCount, Math.max(loadedCount, previous)))
    );
    setPendingSidebarVisibleCount(0);
  }, [activeQuestionsData?.pagination?.nextPage, pendingSidebarVisibleCount, questionOrder.length]);

  const requestNextQuestionsPage = () => {
    const nextPage = Number(activeQuestionsData?.pagination?.nextPage || 0);
    const hasMoreDetails = totalQuestionCount <= 0 || questionOrder.length < totalQuestionCount;
    if (
      nextPage <= 0 ||
      fetchedPagesRef.current.has(nextPage) ||
      fetchingNextPageRef.current ||
      !hasMoreDetails
    ) {
      return false;
    }

    fetchingNextPageRef.current = true;
    setIsFetchingNextPage(true);
    setCurrentPage(nextPage);
    return true;
  };

  const requestQuestionPage = (targetPage) => {
    const normalizedTargetPage = Number(targetPage || 0);
    const totalPages = Number(activeQuestionsData?.pagination?.totalPages || 0);
    if (!normalizedTargetPage) {
      return false;
    }

    if (normalizedTargetPage === Number(currentPage)) {
      return true;
    }

    if (fetchingNextPageRef.current || fetchedPagesRef.current.has(normalizedTargetPage)) {
      return false;
    }

    if (totalPages > 0 && normalizedTargetPage > totalPages) {
      return false;
    }

    fetchingNextPageRef.current = true;
    setIsFetchingNextPage(true);
    setCurrentPage(normalizedTargetPage);
    return true;
  };

  const ensureQuestionDetailsLoaded = (questionId) => {
    const normalizedQuestionId = Number(questionId || 0);
    if (!normalizedQuestionId || questionsById[normalizedQuestionId]) {
      return true;
    }

    const paletteIndex = paletteItems.findIndex(
      (item) => Number(item.id) === normalizedQuestionId
    );
    if (paletteIndex < 0) {
      return false;
    }

    const resolvedPageSize = Math.max(
      1,
      Number(pageSizeRef.current || activeQuestionsData?.pagination?.perPage || 0)
    );
    const targetPage = Math.max(1, Math.floor(paletteIndex / resolvedPageSize) + 1);

    if (Number(currentPage) === targetPage) {
      return false;
    }

    const requested = requestQuestionPage(targetPage);
    if (requested) {
      setPendingPaletteQuestionId(normalizedQuestionId);
    }
    return false;
  };

  const ensureQuestionSequenceLoaded = (sequence) => {
    const normalizedSequence = Number(sequence || 0);
    if (!normalizedSequence) {
      return false;
    }

    const existingQuestion = paletteItems.find(
      (item) =>
        Number(item?.serialNo || item?.question_index || 0) === normalizedSequence &&
        Number(item?.id || 0) > 0
    );

    if (existingQuestion?.id) {
      return ensureQuestionDetailsLoaded(existingQuestion.id);
    }

    const resolvedPageSize = Math.max(
      1,
      Number(pageSizeRef.current || activeQuestionsData?.pagination?.perPage || 0)
    );
    const targetPage = Math.max(1, Math.floor((normalizedSequence - 1) / resolvedPageSize) + 1);
    if (Number(currentPage) === targetPage) {
      return false;
    }

    const requested = requestQuestionPage(targetPage);
    if (requested) {
      setPendingPaletteSequence(normalizedSequence);
    }
    return false;
  };

  useEffect(() => {
    const loadedCount = Number(questionOrder.length || 0);
    if (loadedCount >= sidebarVisibleCount) {
      return;
    }
    requestNextQuestionsPage();
  }, [questionOrder.length, sidebarVisibleCount, totalQuestionCount]);

  useEffect(() => {
    if (questionOrder.length < 2) {
      return;
    }
    if (currentQuestionIndex < questionOrder.length - 2) {
      return;
    }
    requestNextQuestionsPage();
  }, [currentQuestionIndex, questionOrder.length, totalQuestionCount]);

  useEffect(() => {
    if (pendingNextIndex === null) {
      return;
    }
    if (questionOrder.length <= pendingNextIndex) {
      return;
    }
    setCurrentQuestionId(questionOrder[pendingNextIndex]);
    setPendingNextIndex(null);
  }, [pendingNextIndex, questionOrder]);

  useEffect(() => {
    const normalizedQuestionId = Number(pendingPaletteQuestionId || 0);
    if (!normalizedQuestionId || !questionsById[normalizedQuestionId]) {
      return;
    }

    setCurrentQuestionId(normalizedQuestionId);
    setPendingPaletteQuestionId(0);
  }, [pendingPaletteQuestionId, questionsById]);

  useEffect(() => {
    const normalizedSequence = Number(pendingPaletteSequence || 0);
    if (!normalizedSequence) {
      return;
    }

    const matchedQuestionId = questionOrder.find((questionId) => {
      const question = questionsById[questionId];
      return Number(question?.serialNo || question?.question_index || 0) === normalizedSequence;
    });

    if (!matchedQuestionId) {
      return;
    }

    setCurrentQuestionId(Number(matchedQuestionId));
    setPendingPaletteSequence(0);
  }, [pendingPaletteSequence, questionOrder, questionsById]);

  const handleQuestionPaletteOpen = () => {
    paletteQuery.refetch();
    if (isPaletteDrawerEnabled) {
      setIsPaletteDrawerOpen(true);
    }
  };

  const syncQuestionPatch = (questionId, patch) => {
    localQuestionsRef.current.add(questionId);
    setQuestionsById((current) => ({
      ...current,
      [questionId]: mergeQuestion(current[questionId], {
        ...current[questionId],
        ...patch,
      }),
    }));
  };

  const persistTime = async (questionId, viewCount = 0) => {
    const latestQuestionsById = latestQuestionsByIdRef.current || {};
    const seconds = Number(latestQuestionTimersRef.current?.[questionId] || 0);
    if (!questionId || (viewCount !== 1 && (latestQuestionsById[questionId]?.isAnswered || latestQuestionsById[questionId]?.selectedOptionId))) {
      return;
    }

    try {
      await saveTime.mutateAsync({
        question_id: Number(questionId),
        timer_seconds: seconds,
        is_view: Number(viewCount || 0),
      });
    } catch (_error) {
      // best effort
    }
  };

  useEffect(() => {
    const questionId = Number(currentQuestion?.id || 0);
    if (!questionId || enteredQuestionRef.current === questionId) {
      return;
    }

    enteredQuestionRef.current = questionId;
    const languages = currentQuestion.raw?.available_languages;
    const first = Array.isArray(languages) ? languages[0] : String(languages || "").split(",")[0];
    if (/^(hi|hindi)$/i.test(String(first).trim())) setLanguage("hi");
    setIsExplanationOpen(true);
    setActiveModal(null);
    notesQuery.refetch();
    persistTime(questionId, 1);
    syncQuestionPatch(questionId, {
      viewCount: Number(currentQuestion?.viewCount || 0) + 1,
    });
  }, [currentQuestion?.id]);

  useEffect(() => {
    const handlePageHidden = () => {
      const questionId = latestCurrentQuestionIdRef.current;
      if (questionId) {
        persistTime(questionId, 0);
      }
    };

    const handleVisibilityChange = () => {
      setPageVisible(document.visibilityState !== "hidden");
      if (document.visibilityState === "hidden") {
        handlePageHidden();
      }
    };

    window.addEventListener("pagehide", handlePageHidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      handlePageHidden();
      window.removeEventListener("pagehide", handlePageHidden);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const goToPaletteItem = async (item) => {
    if (answerInFlightRef.current) return;
    if (!item) {
      return;
    }

    const questionId = Number(item.id || 0);
    const sequence = Number(item.serialNo || item.question_index || 0);

    void persistTime(currentQuestionId, 0);

    if (questionId > 0) {
      if (ensureQuestionDetailsLoaded(questionId)) {
        setCurrentQuestionId(questionId);
      }
    } else if (sequence > 0) {
      ensureQuestionSequenceLoaded(sequence);
    }

    if (isPaletteDrawerEnabled) {
      setIsPaletteDrawerOpen(false);
    }
  };

  const handleSaveCurrentAnswer = async (questionIdArg, selectedOptionIdArg) => {
    const resolvedQuestionId = Number(questionIdArg || currentQuestion?.id || 0);
    const resolvedOptionId = Number(
      selectedOptionIdArg || selectedAnswers[resolvedQuestionId] || 0
    );

    if (questionsById[resolvedQuestionId]?.isAnswered || questionsById[resolvedQuestionId]?.selectedOptionId) return true;
    if (!resolvedQuestionId || !resolvedOptionId) {
      return true;
    }

    try {
      setSavingQuestionId(resolvedQuestionId);
      await saveAnswer.mutateAsync({
        question_id: resolvedQuestionId,
        user_answer_id: resolvedOptionId,
        is_guess: guessFlags[resolvedQuestionId] ? 1 : 0,
        state_id: Number(questionBaseParams.states_id || questionBaseParams.state_id || 0),
        ...(Number(questionBaseParams.exam_id || 0) > 0 ? { exam_id: Number(questionBaseParams.exam_id) } : {}),
        subject_id: Number(questionBaseParams.subject_id || 0),      });
      setSelectedAnswers((current) => ({ ...current, [resolvedQuestionId]: resolvedOptionId }));
      const patchedOptions = (questionsById[resolvedQuestionId]?.options || []).map((option) => ({
        ...option,
        isSelected: Number(option.id) === resolvedOptionId,
        userSelectedOption: Number(option.id) === resolvedOptionId ? 1 : 0,
      }));
      syncQuestionPatch(resolvedQuestionId, {
        selectedOptionId: resolvedOptionId,
        isAnswered: true,
        isGuess: Boolean(guessFlags[resolvedQuestionId]),
        answeredCreatedAt: "Solved",
        options: patchedOptions,
      });
      setIsExplanationOpen(true);
      queryClient.invalidateQueries({ queryKey: ["questionBankPracticePalette"] });
      queryClient.invalidateQueries({ queryKey: ["questionBankQuestions"] });
      if (soundEnabled && String(questionsById[resolvedQuestionId]?.free_paid).toUpperCase() === "F") {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          try {
            const audio = new AudioContext();
            const tone = audio.createOscillator();
            const gain = audio.createGain();
            tone.frequency.value = patchedOptions.find((option) => option.isSelected)?.isCorrect ? 660 : 220;
            gain.gain.value = 0.08;
            tone.connect(gain); gain.connect(audio.destination);
            tone.start(); tone.stop(audio.currentTime + 0.18);
            tone.onended = () => audio.close();
          } catch (_error) { /* Audio is optional. */ }
        }
      }
      return true;
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to save answer",
        text: error?.message || "Please try again.",
      });
      return false;
    } finally {
      setSavingQuestionId(0);
    }
  };

  const showPaidQuestionPrompt = async () => {
    const result = await Swal.fire({
      icon: "info", title: "Paid Question",
      text: "Subscribe to answer this question.",
      showCancelButton: true, confirmButtonText: "Subscribe", cancelButtonText: "Cancel",    });
    if (result.isConfirmed) {
      const target = questionSubscriptionTarget(currentQuestion, { ...practiceFlowState, ...questionBaseParams }, activeQuestionsData?.raw);
      navigate(target.pathname, { state: target.state });
    }  };

  const handleSelectOption = async (optionId) => {
    if (!currentQuestion?.id || currentQuestionAnswered || answerInFlightRef.current) return;
    answerInFlightRef.current = true;
    setSavingQuestionId(currentQuestion.id);
    try {
      if (paidQuestionBlocked(currentQuestion)) {
        await showPaidQuestionPrompt();
        return;
      }
      await persistTime(currentQuestion.id, 0);
      await handleSaveCurrentAnswer(currentQuestion.id, optionId);
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Unable to unlock question', text: error.message || 'Please try again.' });
    } finally {
      answerInFlightRef.current = false;
      setSavingQuestionId(0);
    }
  };

  const handleReset = async () => {
    if (!currentQuestionAnswered || answerInFlightRef.current) return;
    const result = await Swal.fire({
      title: "Reset attempts?", text: "This resets practice in this session only. Your saved notes are kept.",
      showDenyButton: true, showCancelButton: true,
      confirmButtonText: "Current question", denyButtonText: "All loaded questions", cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed && !result.isDenied) return;
    const ids = result.isDenied ? questionOrder.filter((id) => questionsById[id]?.isAnswered || questionsById[id]?.selectedOptionId) : [currentQuestionId];
    ids.forEach((id) => {
      syncQuestionPatch(id, resetQuestion(questionsById[id]));
      setSelectedAnswers((current) => ({ ...current, [id]: null }));
      setGuessFlags((current) => ({ ...current, [id]: false }));
      setQuestionTimers((current) => ({ ...current, [id]: 0 }));
    });
    setIsExplanationOpen(false);
  };

  const handleToggleGuess = async (nextValue) => {
    if (!currentQuestion?.id || saveGuess.isPending || answerInFlightRef.current) {
      return;
    }

    setGuessFlags((current) => ({
      ...current,
      [currentQuestion.id]: nextValue,
    }));
    syncQuestionPatch(currentQuestion.id, { isGuess: nextValue });

    try {
      await saveGuess.mutateAsync({
        question_id: Number(currentQuestion.id),
        is_guess: nextValue ? 1 : 0,
      });
    } catch (_error) {
      setGuessFlags((current) => ({
        ...current,
        [currentQuestion.id]: !nextValue,
      }));
      syncQuestionPatch(currentQuestion.id, { isGuess: !nextValue });
    }
  };

  const handleGoPrevious = async () => {
    if (answerInFlightRef.current || currentQuestionIndex <= 0) {
      return;
    }
    void persistTime(currentQuestionId, 0);
    setCurrentQuestionId(questionOrder[currentQuestionIndex - 1]);
  };

  const handleGoBack = async () => {
    if (answerInFlightRef.current) return;
    void persistTime(currentQuestionId, 0);
    navigate(QUESTION_BANK_ROUTES.list, {
      state: {
        ...practiceFlowState,
        refresh_list: Date.now(),
      },
    });
  };

  const handleGoNext = async () => {
    if (answerInFlightRef.current) return;

    void persistTime(currentQuestionId, 0);

    if (currentQuestionIndex >= questionOrder.length - 2) {
      requestNextQuestionsPage();
    }

    if (currentQuestionIndex >= 0 && currentQuestionIndex < questionOrder.length - 1) {
      setCurrentQuestionId(questionOrder[currentQuestionIndex + 1]);
      return;
    }

    if (Number(activeQuestionsData?.pagination?.nextPage || 0) > 0) {
      const targetPage = Number(activeQuestionsData.pagination.nextPage);
      if (!fetchedPagesRef.current.has(targetPage)) {
        setPendingNextIndex(questionOrder.length);
        requestNextQuestionsPage();
        return;
      }
      if (questionOrder.length > currentQuestionIndex + 1) {
        setCurrentQuestionId(questionOrder[currentQuestionIndex + 1]);
      }
      return;
    }
  };

  const handleToggleBookmark = async () => {
    if (!currentQuestion?.id || toggleBookmark.isPending) {
      return;
    }

    const questionId = currentQuestion.id;
    const wasBookmarked = currentQuestion.isBookmarked;
    syncQuestionPatch(questionId, { isBookmarked: !wasBookmarked });
    try {
      await toggleBookmark.mutateAsync({ question_id: Number(questionId) });
    } catch (error) {
      syncQuestionPatch(questionId, { isBookmarked: wasBookmarked });
      Swal.fire({ icon: "error", title: "Unable to update bookmark", text: error.message });
    }
  };

  const handleToggleLike = async () => {
    if (!currentQuestion?.id || toggleLike.isPending) {
      return;
    }

    const questionId = currentQuestion.id;
    try {
      const { payload } = await toggleLike.mutateAsync({ question_id: Number(questionId) });
      syncQuestionPatch(questionId, {
        isLiked: payload?.like_status === undefined ? !currentQuestion.isLiked : Number(payload.like_status) === 1,
        likeCount: Number(payload?.total_likes ?? payload?.total_liked ?? Math.max(0, Number(currentQuestion.likeCount || 0) + (currentQuestion.isLiked ? -1 : 1))),
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Unable to update like", text: error.message });
    }
  };

  const handleDeleteNote = async (note) => {
    const noteId = Number(note?.id || 0);
    const questionId = Number(currentQuestion?.id || 0);

    if (!noteId || !questionId || deleteNote.isPending) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Sticky Note?",
      text: "This note will be removed from your account for this question.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53935",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteNote.mutateAsync({
        note_id: noteId,
        question_id: questionId,
      });

      syncQuestionPatch(questionId, {
        notesCount: Math.max(0, Number(currentQuestion?.notesCount || notesItems.length || 0) - 1),
      });

      await Swal.fire({
        icon: "success",
        title: "Note Deleted",
        text: "Sticky note removed successfully.",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Delete Note",
        text: error?.response?.data?.message || error?.message || "Please try again.",
      });
    }
  };

  const referenceHtml =
    referenceQuery.data?.additional_info ||
    localizedText(referenceQuery.data, "ref_message", language) ||
    referenceQuery.data?.ref_message_en ||
    referenceQuery.data?.ref_message_hi ||
    referenceQuery.data?.description ||
    "";
  const notesItems = notesQuery.data?.items || [];
  const shouldShowAdditionalInfoSection = Boolean(referenceHtml);

  useEffect(() => {
    const qid = Number(currentQuestion?.id || 0);
    if (!qid || !shouldShowAdditionalInfoSection) {
      return;
    }
    if (!currentQuestionAnswered) {
      return;
    }
    if (additionalInfoAutoOpenedRef.current[qid]) {
      return;
    }
    additionalInfoAutoOpenedRef.current[qid] = true;
    setIsExplanationOpen(true);
  }, [currentQuestion?.id, currentQuestionAnswered, shouldShowAdditionalInfoSection]);

  const currentQuestionLabel = `${currentQuestion?.serialNo || 0} of ${
    totalQuestionCount || paletteItems.length || Number(activeQuestionsData?.totalQuestions || questionOrder.length || 0)
  }`;

  const footerStats = [
    { key: "bookmark", icon: "bi-bookmark", label: "Bookmark" },
    {
      key: "like",
      icon: currentQuestion?.isLiked ? "bi-heart-fill" : "bi-heart",
      label: "Like",
      value: String(currentQuestion?.likeCount || 0),
      isActive: Boolean(currentQuestion?.isLiked),
    },
    { key: "view", icon: "bi-eye", label: "View", value: String(currentQuestion?.viewCount || 0), modal: "view" },
    { key: "report", icon: "bi-exclamation-circle", label: "Report", value: String(reportsQuery.isPlaceholderData ? currentQuestion?.reportCount || 0 : reportsQuery.data?.totalReportsLabel ?? currentQuestion?.reportCount ?? 0), modal: "report" },
    { key: "notes", icon: "bi-journal-text", label: "Notes", value: String(notesQuery.data?.totalNotes ?? currentQuestion?.notesCount ?? 0), modal: "notes" },
  ];
  const syncedKnownCount = Math.max(totalQuestionCount, questionOrder.length, paletteItems.length);
  const canShowMorePalette =
    sidebarVisibleCount < syncedKnownCount ||
    (Number(activeQuestionsData?.pagination?.nextPage || 0) > 0 &&
      (totalQuestionCount <= 0 || questionOrder.length < totalQuestionCount));
  const visiblePaletteItems = useMemo(
    () => paletteItems.slice(0, sidebarVisibleCount),
    [paletteItems, sidebarVisibleCount]
  );

  const handleShowMorePalette = () => {
    const nextVisibleCount = sidebarVisibleCount + 50;
    const nextPage = Number(activeQuestionsData?.pagination?.nextPage || 0);

    if (nextVisibleCount > questionOrder.length && nextPage > 0) {
      setPendingSidebarVisibleCount(nextVisibleCount);
      requestNextQuestionsPage();
      return;
    }

    setSidebarVisibleCount(nextVisibleCount);
  };

  const renderFooterNav = (variant) => (
    <div className={`footer-nav-fixed ${variant}`}>
      <div className="qbank-navRow" aria-label="Question navigation">
        <button
          type="button"
          className="qbank-navBtn qbank-navBtn--prev"
          onClick={handleGoPrevious}
          disabled={currentQuestionIndex <= 0 || savingQuestionId > 0}
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
          <span>Previous</span>
        </button>

        <button
          type="button"
          className="qbank-navMenu"
          aria-label="Open question list"
          aria-expanded={isPaletteDrawerOpen}
          onClick={handleQuestionPaletteOpen}
        >
          <i className="bi bi-list" aria-hidden="true" />
        </button>

        <button
          type="button"
          className="qbank-navBtn qbank-navBtn--next"
          onClick={handleGoNext}
          disabled={!currentQuestion || saveAnswer.isPending || savingQuestionId > 0 || (currentQuestionIndex >= questionOrder.length - 1 && !Number(activeQuestionsData?.pagination?.nextPage || 0))}
        >
          <span>{saveAnswer.isPending || savingQuestionId > 0 ? "Saving..." : "Next"}</span>
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </div>

      <div className="qbank-stats">
        {footerStats.map((stat) => (
          <button
            key={stat.key}
            type="button"
            className={`qbank-stat${stat.isActive ? " qbank-stat--active" : ""}`}
            onClick={() => {
              if (stat.key === "bookmark") {
                handleToggleBookmark();
                return;
              }
              if (stat.key === "like") {
                handleToggleLike();
                return;
              }
              if (stat.modal) {
                setActiveModal(stat.modal);
              }
            }}
          >
            <i className={`bi ${stat.icon}`} />
            <span>{stat.label}</span>
            {stat.value ? <strong>{stat.value}</strong> : null}
          </button>
        ))}
      </div>
    </div>
  );

  if (questionsQuery.isLoading && !questionOrder.length && !shouldUseInitialQuestionsPayload) {
    return (
      <HomeDashboardLayout showHeader={false}>
        <QuestionBankPracticeShimmer />
      </HomeDashboardLayout>
    );
  }

  if (questionsQuery.isError && !questionOrder.length && !shouldUseInitialQuestionsPayload) {
    return (
      <div className="qbp-status">
        {questionsQuery.error?.message || "Unable to load the practice screen."}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow: hidden;
          box-sizing: border-box;
          padding-bottom: 35px;
        }
        .homeDashboard-wrapper .page-content {
          height: 100%;
          overflow: hidden;
          padding-right: 0;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="categories-page qbank-detail-page" style={{ background: "#fff" }}>
          <div className="categories-page-header">
            <div>
              <button type="button" className="categories-back-button" onClick={handleGoBack}>
                <i className="bi bi-chevron-left" />
              </button>
            </div>
            <div>
              <h1 className="categories-page-title">Questions</h1>
            </div>
            <div>
              <button type="button" className="qbank-headerProfile" aria-label="Profile actions">
                <i className="bi bi-person-circle" />
              </button>
            </div>
          </div>

          <div className="qb-Detail-content">
            <div className="d-flex qbank-detail-main">
              <section className="qbank-LeftContent" aria-label="Question and answers">
                <div className="qbank-card qbank-card--question">
                  <div className="qbank-questionTop">
                    <span className="qbank-progress">{currentQuestionLabel}</span>

                    <div className="qbank-timer" aria-label="Question timer">
                      <i className="bi bi-clock" aria-hidden="true" />
                      <span>{formatDuration(questionTimers[currentQuestion?.id] || 0)}</span>
                    </div>

                    <div className="qbank-toolbar" aria-label="Question tools">
                      <button type="button" className="qbank-toolbarBtn" aria-label="Bookmark" onClick={handleToggleBookmark}>
                        <i className={`bi ${currentQuestion?.isBookmarked ? "bi-bookmark-fill" : "bi-bookmark"}`} />
                      </button>
                      <button type="button" className="qbank-toolbarBtn" aria-label="Instructions" onClick={() => setIsInstructionsOpen(true)}>
                        <i className="bi bi-info-circle" aria-hidden="true" />
                      </button>
                      <button type="button" className="qbank-toolbarBtn" aria-label="Like" onClick={handleToggleLike}>
                        <i className={`bi ${currentQuestion?.isLiked ? "bi-heart-fill" : "bi-heart"}`} />
                      </button>
                    </div>
                  </div>

                  <div className="data-question-scroll">
                    <div className="qbank-readingTools" aria-label="Reading preferences">
                      <label>Language <select aria-label="Question language" value={language} onChange={(event) => setLanguage(event.target.value)}>
                        <option value="en">EN</option><option value="hi">HI</option>
                      </select></label>
                      <button type="button" aria-label="Decrease font size" disabled={fontSize <= 14} onClick={() => setFontSize((size) => size - 2)}>A−</button>
                      <button type="button" aria-label="Increase font size" disabled={fontSize >= 28} onClick={() => setFontSize((size) => size + 2)}>A+</button>
                      <button type="button" aria-pressed={soundEnabled} onClick={() => setSoundEnabled((enabled) => !enabled)}>Sound {soundEnabled ? "on" : "off"}</button>
                      {currentQuestionAnswered ? <button type="button" onClick={handleReset}>Reset attempt</button> : null}
                    </div>
                    <div className="qbank-readingContent" style={{ "--question-font-size": `${fontSize}px` }} lang={language}>
                    <div className="qbank-questionBlock">
                      <div
                        className="qbank-question"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(currentQuestion?.questionHtml || currentQuestion?.question || "Question not available."),
                        }}
                      />
                      {currentQuestion?.questionStatementOne || currentQuestion?.questionStatementTwo ? (
                        <p className="qbank-questionSub">
                          {stripHtml(
                            `${currentQuestion?.questionStatementOne || ""} ${currentQuestion?.questionStatementTwo || ""}`
                          )}
                        </p>
                      ) : null}
                      {currentQuestion?.questionType === "match_making" ? (
                        <table className="qbank-matchTable">
                          <thead><tr><th>{language === "hi" ? "स्तंभ एक" : "Column One"}</th><th>{language === "hi" ? "स्तंभ दो" : "Column Two"}</th></tr></thead>
                          <tbody>{(Array.isArray(currentQuestion.raw?.match_making_options) ? currentQuestion.raw.match_making_options : Array.isArray(currentQuestion.raw?.match_making_option) ? currentQuestion.raw.match_making_option : []).map((row, index) => (
                            <tr key={row.id || index}>
                              <td dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedText(row, "column_one", language)) }} />
                              <td dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedText(row, "column_two", language)) }} />
                            </tr>
                          ))}</tbody>
                        </table>
                      ) : null}
                      {currentQuestion?.questionImage ? (
                        <img className="qbank-questionImage" src={currentQuestion.questionImage} alt="Question illustration" />
                      ) : null}
                    </div>

                    <div className="qbank-options" role="list" aria-label="Answer options">
                      {(currentQuestion?.options || []).map((option, index) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`qbank-option qbank-option--${toOptionTone(
                            currentQuestion,
                            option,
                            option.id
                          )}`}
                          aria-label={`Option ${toOptionLabel(index)}`}
                          onClick={() => handleSelectOption(option.id)}
                          disabled={currentQuestionAnswered || savingQuestionId > 0}
                        >
                          <span className="qbank-option__badge">{toOptionLabel(index)}</span>
                          <span className="qbank-option__copy">
                            <span
                              className="qbank-option__english"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(option.label || ""),
                              }}
                            />
                          </span>
                        </button>
                      ))}
                    </div>
                    </div>
                    {currentQuestionAnswered ? <p className="qbank-answerFeedback" role="status">
                      {currentQuestion.options.some((option) => Number(option.id) === Number(currentQuestion.selectedOptionId) && option.isCorrect)
                        ? (language === "hi" ? "सही उत्तर" : "Correct answer")
                        : (language === "hi" ? "गलत उत्तर — सही विकल्प दिखाया गया है।" : "Incorrect answer — the correct option is highlighted.")}
                    </p> : null}

                    <label className="qbank-guess" htmlFor="guess-answer">
                      <span className="qbank-guess__check">
                        <input
                          id="guess-answer"
                          type="checkbox"
                          checked={Boolean(guessFlags[currentQuestion?.id])}
                          onChange={(event) => handleToggleGuess(event.target.checked)}
                          disabled={saveGuess.isPending || savingQuestionId > 0}
                        />
                        <span className="qbank-guess__box" aria-hidden="true" />
                      </span>
                      <span className="qbank-guess__label">Guess Answer</span>
                      <button type="button" className="info-btn" aria-label="About Guess Answer" onClick={(event) => {
                        event.preventDefault();
                        Swal.fire({ title: "Guess Answer", text: "Mark an answer as a guess to track which questions you answered without being sure.", confirmButtonText: "Got it" });
                      }}>
                        <i className="bi bi-info-circle-fill" />
                      </button>
                    </label>
                  </div>

                  {renderFooterNav("footer-nav-fixed--desktop")}
                </div>
              </section>

              <aside className="qbank-rightContent" aria-label="Explanation and notes">
                {currentQuestion?.isPyq ? <section className="qbank-sideCard">
                  <div className="qbank-sideCard__head">
                    <h3>PYP</h3>
                    <button
                      type="button"
                      className="qbank-sideCard__toggle"
                      onClick={() => setIsPYPOpen((current) => !current)}
                    >
                      <i className={`bi ${isPYPOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                    </button>
                  </div>
                  {isPYPOpen ? (
                    <div className="qbank-sideCard__body">
                      <div className="qbank-sideCard__subtitle">
                        {(currentQuestion?.pyqMappings || []).length ? (
                          currentQuestion.pyqMappings.map((item, index) => (
                            <span key={`${item?.id || index}`}>
                              {item?.master_exam || item?.exam_name || "Previous Year"} {item?.year || ""}
                            </span>
                          ))
                        ) : (
                          <span>No PYP data available.</span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </section> : null}

                {currentQuestionAnswered && (referenceQuery.isLoading || (!referenceQuery.isError && referenceHtml)) ? <section className="qbank-sideCard">
                  <div className="qbank-sideCard__head">
                    <h3>Explanation</h3>
                    <button
                      type="button"
                      className="qbank-sideCard__toggle"
                      onClick={() => setIsExplanationOpen((current) => !current)}
                    >
                      <i className={`bi ${isExplanationOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                    </button>
                  </div>
                  {isExplanationOpen ? (
                    <div className="qbank-sideCard__body">
                      <div className="qbank-sideCard__subtitle">
                        {!currentQuestionAnswered ? (
                          <span>Answer the question to view the explanation.</span>
                        ) : referenceQuery.isLoading ? (
                          <span className="dashboard-skeleton dashboard-skeleton--line" role="status" aria-label="Loading explanation" />
                        ) : referenceHtml ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(referenceHtml),
                            }}
                          />
                        ) : (
                          <span>No explanation available.</span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </section> : null}

                <section className="qbank-sideCard qbank-sideCard--notes">
                  <div className="qbank-sideCard__head">
                    <h3>Sticky Notes</h3>
                    <button
                      type="button"
                      className="qbank-sideCard__toggle"
                      onClick={() => setIsNotesOpen((current) => !current)}
                    >
                      <i className={`bi ${isNotesOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                    </button>
                  </div>
                  {isNotesOpen && currentQuestionAnswered ? (
                    <div className="qbank-notesList">
                      {notesQuery.isLoading ? (
                        <div className="qbank-note">
                          <p className="qbank-note__text">Loading notes...</p>
                        </div>
                      ) : notesItems.length ? (
                        notesItems.map((note, index) => (
                          <div className="qbank-note" key={note.id || index}>
                            <span className="qbank-note__index">{index + 1}.</span>
                            <p className="qbank-note__text">{note.description}</p>
                            <button
                              type="button"
                              className="qbank-note__close"
                              aria-label="Hide notes"
                              onClick={() => handleDeleteNote(note)}
                              disabled={deleteNote.isPending}
                            >
                              <i className="bi bi-x" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="qbank-note">
                          <p className="qbank-note__text">No sticky notes available.</p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </section>
              </aside>
            </div>

            {renderFooterNav("footer-nav-fixed--mobile")}

            <div className="Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--desktop">
              <QuestionPalette
                items={visiblePaletteItems}
                summaryItems={paletteItems}
                activeQuestionId={currentQuestionId}
                onSelect={goToPaletteItem}
                canShowMore={canShowMorePalette}
                onShowMore={handleShowMorePalette}
                activeTab={paletteTab}
                onTabChange={setPaletteTab}
                isLoadingMore={isFetchingNextPage}
              />
            </div>
          </div>

          {isPaletteDrawerEnabled ? (
            <>
              <button
                type="button"
                className={`qbank-drawer-backdrop ${isPaletteDrawerOpen ? "is-open" : ""}`}
                aria-label="Close question palette"
                onClick={() => setIsPaletteDrawerOpen(false)}
              />

              <aside
                className={`Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--drawer ${
                  isPaletteDrawerOpen ? "is-open" : ""
                }`}
                aria-hidden={!isPaletteDrawerOpen}
                aria-label="Question palette drawer"
              >
                <div className="qbank-detail-sidebar__head">
                  <h2>Questions</h2>
                  <button
                    type="button"
                    className="qbank-detail-sidebar__close"
                    aria-label="Close question palette"
                    onClick={() => setIsPaletteDrawerOpen(false)}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>

                <div className="qbank-detail-sidebar__body">
                  <QuestionPalette
                    items={visiblePaletteItems}
                    summaryItems={paletteItems}
                    activeQuestionId={currentQuestionId}
                    onSelect={goToPaletteItem}
                    canShowMore={canShowMorePalette}
                    onShowMore={handleShowMorePalette}
                    activeTab={paletteTab}
                    onTabChange={setPaletteTab}
                    isLoadingMore={isFetchingNextPage}
                  />
                </div>
              </aside>
            </>
          ) : null}
        </div>

        {isInstructionsOpen ? (
          <div className="qbp-modal" onClick={() => setIsInstructionsOpen(false)}>
            <div className="qbp-modal__card" onClick={(event) => event.stopPropagation()}>
              <h3 className="qbp-modal__title">Instructions</h3>
              <div className="qbp-modal__text">
                {instructionsQuery.isLoading ? (
                  "Loading instructions..."
                ) : instructionsQuery.data?.description ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(instructionsQuery.data.description),
                    }}
                  />
                ) : (
                  "No instructions available."
                )}
              </div>
              <button
                type="button"
                className="qbp-modal__btn"
                onClick={() => setIsInstructionsOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        ) : null}

        <ViewModal open={activeModal === "view"} onClose={() => setActiveModal(null)} question={currentQuestion} />
        <ReportModal
          open={activeModal === "report"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
          questionQueryParams={questionQueryParams}
        />
        <StickyNotesPopup
          open={activeModal === "notes"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
          questionQueryParams={questionQueryParams}
        />
      </HomeDashboardLayout>
    </>
  );
};

export default QuestionBankPractice;








